import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoEllipsisVertical, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import PropTypes from 'prop-types';
import styles from './styles.module.css';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function ActionsPanel({ options, selectedId, panelId, onUpdate, onDelete, onViewImage, onRetry, onClose }) {
  const firstActionRef = useRef(null);

  useEffect(() => {
    firstActionRef.current?.focus();
  }, []);

  const handleAction = (option) => {
    if (option === 'Update' && selectedId != null) onUpdate(selectedId);
    else if (option === 'Delete' && selectedId != null) onDelete(selectedId);
    else if (option === 'View' && selectedId != null) onViewImage(selectedId);
    else if (option === 'Retry' && selectedId != null) onRetry(selectedId);
    onClose();
  };

  return (
    <div className={styles.actionsPanel} role="menu" id={panelId}>
      {(options || []).map((option, index) => (
        <button
          key={option}
          type="button"
          ref={index === 0 ? firstActionRef : null}
          className={styles.actionsButton}
          onClick={() => handleAction(option)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              onClose();
            }
          }}
          role="menuitem"
        >
          {option}
        </button>
      ))}
    </div>
  );
}

ActionsPanel.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  panelId: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewImage: PropTypes.func,
  onRetry: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ActionsPanel.defaultProps = {
  options: [],
  selectedId: null,
  onViewImage: () => {},
  onRetry: () => {},
};

function getRowId(row) {
  return row?.id ?? row?._id;
}

function getValueByPath(row, path) {
  if (typeof path !== 'string') return undefined;
  if (!path.includes('.')) return row?.[path];
  return path.split('.').reduce((obj, key) => obj?.[key], row);
}

function getCellClassName(column) {
  const className = [column?.mono && styles.cellMono, column?.muted && styles.cellMuted]
    .filter(Boolean)
    .join(' ');
  return className || undefined;
}

function withBadge(cellContent, column) {
  if (!column?.badgeVariant) return cellContent;
  const badgeClass =
    column.badgeVariant === 'success'
      ? styles.statusBadgeSuccess
      : column.badgeVariant === 'pending'
        ? styles.statusBadgePending
        : styles.statusBadgeCancelled;
  return <span className={`${styles.statusBadge} ${badgeClass}`}>{cellContent}</span>;
}

function resolveCellContent(column, row, isChildRow) {
  const value = getValueByPath(row, column.id);
  const baseContent = column.render
    ? column.render(value, row)
    : column.format
      ? column.format(value, row)
      : value;
  const contentWithBadge = withBadge(baseContent, column);
  if (isChildRow && contentWithBadge == null) return '\u00A0';
  return contentWithBadge;
}

function Table({
  columns,
  rows,
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  onPageChange: externalOnPageChange,
  onRowsPerPageChange: externalOnRowsPerPageChange,
  totalCount,
  disableInternalPagination = false,
  collapsibleColumns = null,
  collapsibleTitle = '',
  getRowChildren = null,
  emptyMessage = 'No data available.',
  onViewImage = null,
  onRowClick = null,
}) {
  const navigate = useNavigate();
  const actionsRef = useRef(null);
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);
  const [openActionsKey, setOpenActionsKey] = useState(null);
  const [openRowIds, setOpenRowIds] = useState(new Set());

  const hasCollapsible = useMemo(
    () => Array.isArray(collapsibleColumns) && collapsibleColumns.length > 0,
    [collapsibleColumns]
  );
  const optionsColumn = useMemo(() => columns.find((column) => column.id === 'options'), [columns]);

  const getChildren = useCallback((row) => {
    if (getRowChildren && typeof getRowChildren === 'function') {
      const children = getRowChildren(row);
      return Array.isArray(children) ? children : [];
    }
    return Array.isArray(row.children) ? row.children : [];
  }, [getRowChildren]);

  const toggleRowOpen = useCallback((rowId, e) => {
    if (e) e.stopPropagation();
    setOpenRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!openActionsKey) return undefined;
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setOpenActionsKey(null);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenActionsKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openActionsKey]);

  const page = externalPage !== undefined ? externalPage : internalPage;
  const rowsPerPage =
    externalRowsPerPage !== undefined ? externalRowsPerPage : internalRowsPerPage;

  const handleChangePage = useCallback((newPage) => {
    if (externalOnPageChange) externalOnPageChange(null, newPage);
    else setInternalPage(newPage);
  }, [externalOnPageChange]);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    const value = typeof newRowsPerPage === 'object' ? +newRowsPerPage.target?.value : newRowsPerPage;
    if (externalOnRowsPerPageChange) {
      if (typeof newRowsPerPage === 'object') externalOnRowsPerPageChange(newRowsPerPage);
      else externalOnRowsPerPageChange({ target: { value: String(value) } });
    } else {
      setInternalRowsPerPage(value);
      setInternalPage(0);
    }
  }, [externalOnRowsPerPageChange]);

  const handleUpdate = useCallback((id, table) => {
    switch (table) {
      case 'product':
        navigate(`/inventory/products/update-product/${id}`);
        break;
      case 'stock':
        navigate(`/inventory/stocks/update-stock/${id}`);
        break;
      case 'category':
        navigate(`/inventory/categories/update-category/${id}`);
        break;
      case 'supplier':
        navigate(`/inventory/suppliers/update-supplier/${id}`);
        break;
      case 'shipment':
        navigate(`/delivery/shipments/update-shipment/${id}`);
        break;
      default:
        break;
    }
  }, [navigate]);

  const handleDelete = useCallback((id, column) => {
    const deleteHandler = column?.customHandlers?.onDelete || optionsColumn?.customHandlers?.onDelete;
    if (deleteHandler) {
      deleteHandler(id);
    }
  }, [optionsColumn]);

  const handleViewImage = useCallback((id, column) => {
    const viewImageHandler =
      column?.customHandlers?.onViewImage ||
      optionsColumn?.customHandlers?.onViewImage ||
      onViewImage;
    if (viewImageHandler) {
      viewImageHandler(id);
    }
  }, [optionsColumn, onViewImage]);

  const handleRetry = useCallback((id, column) => {
    const retryHandler = column?.customHandlers?.onRetry || optionsColumn?.customHandlers?.onRetry;
    if (retryHandler) {
      retryHandler(id);
    }
  }, [optionsColumn]);

  const rowsArray = Array.isArray(rows) ? rows : [];
  const total =
    totalCount !== undefined ? totalCount : rowsArray.length;
  const paginatedRows = disableInternalPagination
    ? rowsArray
    : rowsArray.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const from = total === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage + rowsPerPage, total);
  const canPrev = page > 0;
  const canNext = to < total;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  const pageNumbers = useMemo(() => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    const set = new Set([0, totalPages - 1]);
    for (let i = Math.max(0, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      set.add(i);
    }
    const sorted = [...set].sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('ellipsis');
      out.push(sorted[i]);
    }
    return out;
  }, [totalPages, page]);

  const renderActionsCell = useCallback((column, row, scope) => {
    const rowId = getRowId(row);
    const options = column?.options || [];
    const key = `${scope}-${String(rowId ?? 'unknown')}`;
    const isOpen = openActionsKey === key;
    const panelId = `row-actions-${key}`;
    return (
      <td
        key={column.id}
        className={styles.cellOptions}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.actionsWrap} ref={isOpen ? actionsRef : null}>
          <button
            type="button"
            className={styles.actionsTrigger}
            onClick={() => setOpenActionsKey(isOpen ? null : key)}
            aria-label="Row actions"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={panelId}
          >
            <IoEllipsisVertical size={18} />
          </button>
          {isOpen && (
            <ActionsPanel
              panelId={panelId}
              options={options}
              selectedId={rowId}
              onUpdate={(id) => {
                if (column.customHandlers?.onUpdate) {
                  column.customHandlers.onUpdate(id);
                } else {
                  handleUpdate(id, column.table);
                }
              }}
              onDelete={(id) => {
                handleDelete(id, column);
              }}
              onViewImage={(id) => {
                handleViewImage(id, column);
              }}
              onRetry={(id) => {
                handleRetry(id, column);
              }}
              onClose={() => setOpenActionsKey(null)}
            />
          )}
        </div>
      </td>
    );
  }, [handleDelete, handleRetry, handleUpdate, handleViewImage, openActionsKey]);

  const renderDataCell = useCallback((column, row, scope) => {
    if (column.id === 'options') {
      return renderActionsCell(column, row, scope);
    }
    const content = resolveCellContent(column, row, scope === 'child');
    return (
      <td
        key={column.id}
        className={getCellClassName(column)}
        style={{ textAlign: column.align || 'left' }}
      >
        {content}
      </td>
    );
  }, [renderActionsCell]);

  const hasNoData = rowsArray.length === 0;
  if (hasNoData) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.emptyStateBlock}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label="data table">
          <thead>
            <tr>
              {hasCollapsible && (
                <th className={styles.cellExpand} aria-label="Expand" />
              )}
              {columns.map((col) => (
                <th
                  key={col.id}
                  style={{
                    minWidth: col.minWidth,
                    textAlign: col.align || 'left',
                  }}
                >
                  {col.sortable && col.onSort ? (
                    <button
                      type="button"
                      className={styles.sortableHeaderBtn}
                      onClick={col.onSort}
                    >
                      {col.label} {col.sortIcon}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, rowIndex) => {
              const rowId = getRowId(row);
              const children = hasCollapsible ? getChildren(row) : [];
              const isOpen = openRowIds.has(rowId);
              const rowKey = rowId ?? `row-${rowIndex}`;
              return (
                <React.Fragment key={rowKey}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {hasCollapsible && (
                      children.length > 0 ? (
                        <td className={styles.cellExpand}>
                          <button
                            type="button"
                            className={styles.expandBtn}
                            onClick={(e) => toggleRowOpen(rowId, e)}
                            aria-expanded={isOpen}
                            aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                            aria-controls={`row-details-${rowKey}`}
                          >
                            {isOpen ? (
                              <IoChevronUp size={18} />
                            ) : (
                              <IoChevronDown size={18} />
                            )}
                          </button>
                        </td>
                      ) : (
                        <td className={styles.cellExpand} />
                      )
                    )}
                    {columns.map((column) => renderDataCell(column, row, 'parent'))}
                  </tr>
              {hasCollapsible && children.length > 0 && (
                <tr className={styles.collapseRow} aria-hidden={!isOpen}>
                  <td
                    className={styles.collapseCell}
                    colSpan={columns.length + (hasCollapsible ? 1 : 0)}
                  >
                    <div
                      className={
                        isOpen
                          ? `${styles.collapsePanel} ${styles.collapsePanelOpen}`
                          : `${styles.collapsePanel} ${styles.collapsePanelClosed}`
                      }
                      id={`row-details-${rowKey}`}
                      role="region"
                      aria-label="Row details"
                    >
                      <div className={styles.nestedTableWrap}>
                        {(typeof collapsibleTitle === 'function'
                          ? collapsibleTitle(row)
                          : collapsibleTitle) && (
                          <div className={styles.collapsibleTitle}>
                            {typeof collapsibleTitle === 'function'
                              ? collapsibleTitle(row)
                              : collapsibleTitle}
                          </div>
                        )}
                        <table className={styles.nestedTable} aria-label="Child rows">
                          <thead>
                            <tr>
                              {collapsibleColumns.map((col) => (
                                <th
                                  key={col.id}
                                  style={{
                                    minWidth: col.minWidth,
                                    textAlign: col.align || 'left',
                                  }}
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {children.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={collapsibleColumns.length}
                                  className={styles.nestedEmpty}
                                >
                                  No items
                                </td>
                              </tr>
                            ) : (
                              children.map((child, idx) => (
                                <tr key={getRowId(child) ?? `${rowKey}-child-${idx}`}>
                                  {collapsibleColumns.map((column) =>
                                    renderDataCell(column, child, 'child')
                                  )}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.tableFooter}>
        <div className={styles.tableFooterLeft}>
          <div className={styles.rowsToggleWrap}>
            <span className={styles.rowsToggleLabel}>Rows per page</span>
            <div
              className={styles.rowsToggle}
              role="group"
              aria-label="Rows per page"
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.rowsToggleBtn} ${rowsPerPage === n ? styles.rowsToggleBtnActive : ''}`.trim()}
                  onClick={() => handleChangeRowsPerPage(n)}
                  aria-pressed={rowsPerPage === n}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <span className={styles.tableInfo}>
            Showing {from}-{to} of {total}
          </span>
        </div>
        <div className={styles.tableFooterRight}>
          <div
            className={styles.pagination}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' && canPrev) {
                event.preventDefault();
                handleChangePage(page - 1);
              }
              if (event.key === 'ArrowRight' && canNext) {
                event.preventDefault();
                handleChangePage(page + 1);
              }
            }}
          >
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canPrev}
              onClick={() => handleChangePage(page - 1)}
              aria-label="Previous page"
            >
              ←
            </button>
            {pageNumbers.map((p, i) =>
              p === 'ellipsis' ? (
                <span key={`ellipsis-${i}`} className={styles.pageDots}>
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`${styles.pageBtn} ${page === p ? styles.pageBtnActive : ''}`.trim()}
                  onClick={() => handleChangePage(p)}
                  aria-label={`Page ${p + 1}`}
                >
                  {p + 1}
                </button>
              )
            )}
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canNext}
              onClick={() => handleChangePage(page + 1)}
              aria-label="Next page"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      minWidth: PropTypes.number,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      mono: PropTypes.bool,
      muted: PropTypes.bool,
      format: PropTypes.func,
      render: PropTypes.func,
      badgeVariant: PropTypes.oneOf(['success', 'pending', 'cancelled']),
      sortable: PropTypes.bool,
      onSort: PropTypes.func,
      sortIcon: PropTypes.node,
      options: PropTypes.arrayOf(PropTypes.string),
      table: PropTypes.string,
      customHandlers: PropTypes.shape({
        onUpdate: PropTypes.func,
        onDelete: PropTypes.func,
        onViewImage: PropTypes.func,
        onRetry: PropTypes.func,
      }),
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  totalCount: PropTypes.number,
  disableInternalPagination: PropTypes.bool,
  collapsibleColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      minWidth: PropTypes.number,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      format: PropTypes.func,
      render: PropTypes.func,
      badgeVariant: PropTypes.oneOf(['success', 'pending', 'cancelled']),
      mono: PropTypes.bool,
      muted: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string),
      table: PropTypes.string,
      customHandlers: PropTypes.shape({
        onUpdate: PropTypes.func,
        onDelete: PropTypes.func,
        onViewImage: PropTypes.func,
        onRetry: PropTypes.func,
      }),
    })
  ),
  collapsibleTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  getRowChildren: PropTypes.func,
  emptyMessage: PropTypes.string,
  onViewImage: PropTypes.func,
  onRowClick: PropTypes.func,
};

Table.defaultProps = {
  rows: [],
};

export default Table;
